import { ABSENCE_TYPES, createAbsenceSchema, type CreateAbsenceInput } from '@hirebotiq-ops/core';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { Field } from '../components/ui/field.js';
import { Input } from '../components/ui/input.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select.js';
import { requireRole } from '../lib/auth-guard.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/worker/absences')({
  component: WorkerAbsences,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['worker']);
  },
});

const TYPE_LABEL: Record<string, string> = {
  vacation: 'Urlaub',
  sick: 'Krankheit',
  special: 'Sonderurlaub',
  unpaid: 'Unbezahlt',
};

function WorkerAbsences() {
  const utils = trpc.useUtils();
  const list = trpc.absences.listMine.useQuery();
  const quota = trpc.absences.myQuota.useQuery();

  const request = trpc.absences.request.useMutation({
    onSuccess: () => {
      toast.success('Antrag eingereicht.');
      utils.absences.listMine.invalidate();
      utils.absences.myQuota.invalidate();
      form.reset({ type: undefined, startDate: '', endDate: '', reason: '' } as never);
    },
    onError: (e) => toast.error(e.message),
  });
  const cancel = trpc.absences.cancelMine.useMutation({
    onSuccess: () => {
      utils.absences.listMine.invalidate();
      utils.absences.myQuota.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const form = useForm<CreateAbsenceInput>({ resolver: zodResolver(createAbsenceSchema) });
  const { register, handleSubmit, setValue, watch, formState } = form;

  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const conflictCheck = trpc.absences.checkConflicts.useQuery(
    { startDate: startDate ?? '', endDate: endDate ?? '' },
    { enabled: !!startDate && !!endDate },
  );

  return (
    <div className="min-h-screen p-6 max-w-3xl mx-auto space-y-6">
      <Link to="/worker" className="text-sm text-[var(--muted-foreground)] hover:text-foreground">
        ← Zurück zur Übersicht
      </Link>
      <h1 className="text-3xl font-bold">Meine Abwesenheiten</h1>

      {/* Urlaubs-Kontingents-Karte */}
      {quota.data && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Urlaubskontingent {quota.data.year}</CardTitle>
            <CardDescription>Deine Jahresübersicht über Urlaubstage &amp; Fehlzeiten</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="rounded-md border p-3 bg-[var(--card)]">
                <div className="text-xs text-[var(--muted-foreground)]">Gesamtanspruch</div>
                <div className="text-xl font-bold">{quota.data.totalAllowedDays} Tage</div>
              </div>
              <div className="rounded-md border p-3 bg-[var(--card)]">
                <div className="text-xs text-[var(--muted-foreground)]">Genehmigt</div>
                <div className="text-xl font-bold text-[oklch(0.55_0.15_145)]">
                  {quota.data.approvedVacationDays} Tage
                </div>
              </div>
              <div className="rounded-md border p-3 bg-[var(--card)]">
                <div className="text-xs text-[var(--muted-foreground)]">Beantragt</div>
                <div className="text-xl font-bold text-[oklch(0.6_0.15_85)]">
                  {quota.data.requestedVacationDays} Tage
                </div>
              </div>
              <div className="rounded-md border p-3 bg-[var(--card)]">
                <div className="text-xs text-[var(--muted-foreground)]">Verbleibend</div>
                <div className="text-xl font-bold text-[var(--primary)]">
                  {quota.data.remainingDays} Tage
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Neuen Antrag stellen</CardTitle>
          <CardDescription>Urlaub, Krankheit oder Sonderurlaub</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-4 md:grid-cols-2"
            onSubmit={handleSubmit((v) => request.mutate(v))}
          >
            <Field label="Typ *" htmlFor="type" error={formState.errors.type?.message}>
              <Select value={watch('type')} onValueChange={(v) => setValue('type', v as CreateAbsenceInput['type'])}>
                <SelectTrigger id="type">
                  <SelectValue placeholder="Typ wählen" />
                </SelectTrigger>
                <SelectContent>
                  {ABSENCE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TYPE_LABEL[t] ?? t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div />
            <Field label="Von *" htmlFor="startDate" error={formState.errors.startDate?.message}>
              <Input id="startDate" type="date" {...register('startDate')} />
            </Field>
            <Field label="Bis *" htmlFor="endDate" error={formState.errors.endDate?.message}>
              <Input id="endDate" type="date" {...register('endDate')} />
            </Field>

            {/* Live Kollisions-Check */}
            {conflictCheck.data &&
              (conflictCheck.data.hasAbsenceConflict || conflictCheck.data.hasAssignmentConflict) && (
                <div className="md:col-span-2 rounded-md bg-[oklch(0.95_0.08_85)] p-3 border border-[oklch(0.85_0.15_85)] space-y-1 text-sm text-[oklch(0.3_0.15_85)]">
                  <p className="font-semibold flex items-center gap-2">
                    ⚠️ Terminüberschneidung bei diesem Zeitraum:
                  </p>
                  {conflictCheck.data.hasAssignmentConflict && (
                    <p>
                      Du hast in diesem Zeitraum aktiven Einsatz bei:{' '}
                      {conflictCheck.data.conflictingAssignments
                        .map((a) => a.clientCompanyName ?? 'Kunde')
                        .join(', ')}
                    </p>
                  )}
                  {conflictCheck.data.hasAbsenceConflict && (
                    <p>
                      Es existiert bereits ein Abwesenheitsantrag im Zeitraum.
                    </p>
                  )}
                </div>
              )}

            <Field label="Grund (optional)" htmlFor="reason">
              <Input id="reason" {...register('reason')} />
            </Field>
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" disabled={request.isPending}>
                {request.isPending ? 'Sende …' : 'Antrag einreichen'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Meine Anträge</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {list.data?.length === 0 && (
            <p className="text-sm text-[var(--muted-foreground)]">Noch keine Anträge.</p>
          )}
          {list.data?.map((a) => (
            <div
              key={a.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--border)] p-3"
            >
              <div className="text-sm">
                <span className="font-medium">{TYPE_LABEL[a.type] ?? a.type}</span>{' '}
                <span className="text-[var(--muted-foreground)]">
                  {a.startDate} – {a.endDate}
                </span>
                <span className="ml-2">
                  <StatusBadge status={a.status} />
                </span>
              </div>
              {a.status === 'requested' && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={cancel.isPending}
                  onClick={() => cancel.mutate({ absenceId: a.id })}
                >
                  Zurückziehen
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
